import { z } from 'zod'

export const phoneSchema = z.object({
  phone: z.string().superRefine((val, ctx) => {
    if (!/^\d{10}$/.test(val)) {
      ctx.addIssue({ code: 'custom', message: 'Enter a valid 10-digit mobile number.' })
      return
    }
    if (!/^[6-9]/.test(val)) {
      ctx.addIssue({ code: 'custom', message: 'Enter a valid Indian mobile number.' })
    }
  }),
})
export type PhoneFormValues = z.infer<typeof phoneSchema>

export const otpSchema = z.object({
  otp: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code.'),
})
export type OtpFormValues = z.infer<typeof otpSchema>

export const emailSignInSchema = z.object({
  email: z.string().email('Enter a valid email address.'),
  password: z.string().min(1, 'Enter your password.'),
})
export type EmailSignInFormValues = z.infer<typeof emailSignInSchema>

export const forgotPasswordSchema = z.object({
  email: z.string().email('Enter a valid email address.'),
})
export type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>

export const PASSWORD_REQUIREMENTS_HINT =
  'Use 8+ characters with uppercase, lowercase, a number, and a special character.'

// Shared by every "set a new password" form (signup, admin/investor reset, investor
// change-password) so the complexity rule can't drift between them.
const passwordComplexitySchema = z
  .string()
  .min(8, 'Use at least 8 characters.')
  .regex(/[a-z]/, 'Include at least one lowercase letter.')
  .regex(/[A-Z]/, 'Include at least one uppercase letter.')
  .regex(/\d/, 'Include at least one number.')
  .regex(/[^A-Za-z0-9]/, 'Include at least one special character.')

export const setPasswordSchema = z
  .object({
    password: passwordComplexitySchema,
    confirmPassword: z.string().min(1, 'Re-enter your password.'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  })
export type SetPasswordFormValues = z.infer<typeof setPasswordSchema>

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password.'),
    newPassword: passwordComplexitySchema,
    confirmNewPassword: z.string().min(1, 'Re-enter your new password.'),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match.',
    path: ['confirmNewPassword'],
  })
export type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>
